import React, { createContext, useContext, useState, useRef, useCallback, ReactNode, Dispatch, SetStateAction } from 'react';
import { SimulationRunSummary } from '../types/simulation';
import { AppError } from '../types/error.d';
import { LoggingService } from '../services/logging/loggingService';
import { SeverityLevel } from '../services/logging/types';

/**
 * Status of the current simulation.
 */
export type SimulationStatus = 'idle' | 'running' | 'finished' | 'failed';

/**
 * Status for each simulation step.
 */
export type StepStatus = 'notStarted' | 'inProgress' | 'success' | 'failed';

/**
 * One simulation in a multi-simulation run. The single-run fields above always
 * describe whichever queued run is active.
 */
export type QueuedRunStatus = 'queued' | 'running' | 'finished' | 'failed' | 'cancelled';
export interface QueuedRun {
  key: string;
  name: string;
  status: QueuedRunStatus;
  /** Why a failed run failed; the drawer's error card only keeps the latest run's error */
  error?: string;
}

/**
 * Simulation context shape.
 */
export interface SimulationContextType {
  /** Simulation scenario name */
  simulationName: string;
  setSimulationName: (name: string) => void;
  /** Simulation scenario ID */
  simulationId: string;
  setSimulationId: (id: string) => void;
  /** Number of vehicles in the simulation */
  numberOfVehicles: number;
  setNumberOfVehicles: (n: number) => void;
  /** Number of rides in the simulation */
  numberOfRides: number;
  setNumberOfRides: (n: number) => void;
  /** Time settings preset label */
  timeSettingsPreset: string;
  setTimeSettingsPreset: (preset: string) => void;
  /** Status for each simulation step */
  stepStatuses: StepStatus[];
  setStepStatuses: (statuses: StepStatus[]) => void;
  /** Overall simulation status */
  status: SimulationStatus;
  setStatus: (status: SimulationStatus) => void;
  /** Simulation error (structured) */
  simulationError: AppError | null;
  setSimulationError: (error: AppError | null) => void;
  /** Locally cached simulation runs for the drawer */
  simulationHistory: SimulationRunSummary[];
  setSimulationHistory: Dispatch<SetStateAction<SimulationRunSummary[]>>;
  /** Simulations of the current multi-simulation run, in run order; empty for a single run */
  runQueue: QueuedRun[];
  setRunQueue: Dispatch<SetStateAction<QueuedRun[]>>;
  /** Cancels the runs still queued; the running one finishes */
  cancelQueuedRuns: () => void;
  /** Read by the run loop after each run, so a cancel from the drawer is seen even if the modal unmounted */
  isRunCancelled: (key: string) => boolean;
}

const defaultStepStatuses: StepStatus[] = [
  'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted', 'notStarted'
];

const SimulationContext = createContext<SimulationContextType | undefined>(undefined);

/**
 * SimulationProvider wraps children with simulation state.
 */
export const SimulationProvider = ({ children }: { children: ReactNode }) => {
  const [simulationName, setSimulationName] = useState('');
  const [simulationId, setSimulationId] = useState('');
  const [numberOfVehicles, setNumberOfVehicles] = useState(0);
  const [numberOfRides, setNumberOfRides] = useState(0);
  const [timeSettingsPreset, setTimeSettingsPreset] = useState('');
  const [stepStatuses, setStepStatuses] = useState<StepStatus[]>(defaultStepStatuses);
  const [status, setStatus] = useState<SimulationStatus>('idle');
  const [simulationError, setSimulationError] = useState<AppError | null>(null);
  const [simulationHistory, setSimulationHistory] = useState<SimulationRunSummary[]>([]);
  const [runQueue, setRunQueue] = useState<QueuedRun[]>([]);
  const runQueueRef = useRef(runQueue);
  runQueueRef.current = runQueue;

  const cancelQueuedRuns = useCallback(() => {
    setRunQueue(queue => queue.map(run => (run.status === 'queued' ? { ...run, status: 'cancelled' } : run)));
  }, []);
  const isRunCancelled = useCallback(
    (key: string) => runQueueRef.current.find(run => run.key === key)?.status === 'cancelled',
    []
  );

  return (
    <SimulationContext.Provider
      value={{
        simulationName,
        setSimulationName,
        simulationId,
        setSimulationId,
        numberOfVehicles,
        setNumberOfVehicles,
        numberOfRides,
        setNumberOfRides,
        timeSettingsPreset,
        setTimeSettingsPreset,
        stepStatuses,
        setStepStatuses,
        status,
        setStatus,
        simulationError,
        setSimulationError,
        simulationHistory,
        setSimulationHistory,
        runQueue,
        setRunQueue,
        cancelQueuedRuns,
        isRunCancelled,
      }}
    >
      {children}
    </SimulationContext.Provider>
  );
};

/**
 * useSimulation provides access to the simulation context.
 */
export function useSimulation() {
  const ctx = useContext(SimulationContext);
  if (!ctx) {
    const loggingService = LoggingService.getInstance();
    const errorMessage = 'useSimulation must be used within a SimulationProvider';
    loggingService.critical(errorMessage, { component: 'useSimulationHook' });
    throw new Error(errorMessage);
  }
  return ctx;
}

export { SimulationContext };